import { useCallback } from "react";

import { generateCustomLinkId } from "@/components/custom-links/generate-custom-link-id";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { type CustomLink, useSettings } from "@/providers/general/settings-provider";

function normalizeLinks(links: unknown): CustomLink[] {
    if (!Array.isArray(links)) return [];
    return links.filter(
        (l): l is CustomLink =>
            l != null &&
            typeof l === "object" &&
            typeof (l as CustomLink).id === "string" &&
            typeof (l as CustomLink).label === "string" &&
            typeof (l as CustomLink).url === "string" &&
            (typeof (l as CustomLink).iconName === "string" || (l as CustomLink).iconName === null)
    );
}

export type { CustomLink };

export function useCustomLinks() {
    const { currentSettings, setValue } = useSettings();
    const links = normalizeLinks(currentSettings.CUSTOM_LINKS);

    const addLink = useCallback(
        (link: Omit<CustomLink, "id">) => {
            const newLink: CustomLink = { ...link, id: generateCustomLinkId() };
            setValue(SETTINGS_KEYS.CUSTOM_LINKS, [...links, newLink]);
            return newLink.id;
        },
        [links, setValue]
    );

    const updateLink = useCallback(
        (id: string, updates: Partial<Omit<CustomLink, "id">>) => {
            const next = links.map((l) => (l.id === id ? { ...l, ...updates } : l));
            setValue(SETTINGS_KEYS.CUSTOM_LINKS, next);
        },
        [links, setValue]
    );

    const removeLink = useCallback(
        (id: string) => {
            setValue(
                SETTINGS_KEYS.CUSTOM_LINKS,
                links.filter((l) => l.id !== id)
            );
        },
        [links, setValue]
    );

    return { links, addLink, updateLink, removeLink };
}
